import axios from "axios";
import React, { useEffect, useState } from "react";
import { TbBuildingStadium } from "react-icons/tb";
import {
  RiFootballLine,
  RiArticleLine,
  RiUserVoiceLine,
  RiCalendarEventLine,
  RiCheckboxCircleLine,
  RiTimerLine,
  RiQuestionAnswerLine,
} from "react-icons/ri";

import { useNavigate } from "react-router-dom";

const SkeletonStatsCard = () => (
  <div className="bg-white shadow-lg rounded-2xl p-6 flex items-center justify-between border border-primaryTextColor animate-pulse">
    <div className="space-y-2 w-full">
      <div className="h-4 bg-gray-300 rounded w-2/3"></div>
      <div className="h-6 bg-gray-300 rounded w-1/2"></div>
    </div>
    <div className="h-8 w-8 bg-gray-300 rounded-full"></div>
  </div>
);
const SkeletonEventCard = () => (
  <div className="relative rounded-2xl overflow-hidden shadow-lg animate-pulse">
    <div className="w-full h-48 bg-gray-300"></div>
    <div className="absolute inset-0 bg-black bg-opacity-40 p-4 flex flex-col justify-end">
      <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
      <div className="h-3 bg-gray-300 rounded w-1/3 mb-1"></div>
      <div className="h-3 bg-gray-300 rounded w-1/4 mb-1"></div>
      <div className="h-3 bg-gray-300 rounded w-1/5"></div>
    </div>
  </div>
);

const SkeletonQuickActionButton = () => (
  <div className="h-20 bg-gray-300 rounded-2xl animate-pulse"></div>
);

const EmptyUpcomingEvents = () => (
  <div className="col-span-full flex flex-col items-center justify-center py-12 px-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg border border-dashed border-primaryBtnColor">
    {/* Icon */}
    <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor shadow-md mb-6">
      <RiCalendarEventLine className="w-10 h-10 text-white" />
    </div>

    {/* Title */}
    <h3 className="text-2xl font-heading font-bold text-primaryTextColor mb-2">
      No Upcoming Events
    </h3>

    {/* Subtitle */}
    <p className="text-sm text-secondaryTextColor mb-6 text-center max-w-md">
      It looks a little quiet right now. New matches and events will show up
      here as soon as they’re scheduled.
    </p>

  </div>
);

const defaultDashboardData = {
  totalFields: 0,
  totalBookings: 0,
  upcomingMatches: 0,
  totalUsers: 0,
  upcomingEvents: [
    {
      id: "",
      eventType: "",
      title: "",
      field: {
        id: "",
        name: "",
        imageUrl: "",
        price: 0,
      },
      date: "",
      status: "",
    },
  ],
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(defaultDashboardData);

  const quickActions = [
    { label: "All Our Field", path: "/all-fields" },
    { label: "Manage Events", path: "/events-bookings" },
    { label: "News & Blogs", path: "/news-blogs" },
    { label: "Sports Categories", path: "/sports-categories" },
  ];
  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/all`
      );
      // console.log("data", data);
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6 tracking-wide">
      <div className="">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
            Dashboard
          </h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
          {loading
            ? Array(8)
                .fill(0)
                .map((_, i) => <SkeletonStatsCard key={i} />)
            : [
                {
                  label: "Total Fields",
                  value: stats.totalFields,
                  icon: (
                    <TbBuildingStadium
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
                {
                  label: "Total Sports",
                  value: stats.totalSports,
                  icon: (
                    <RiFootballLine
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
                {
                  label: "Total NewsBlogs",
                  value: stats.totalNewsBlogs,
                  icon: (
                    <RiArticleLine
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
                {
                  label: "Total Testimonials",
                  value: stats.totalTestimonial,
                  icon: (
                    <RiUserVoiceLine
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
                {
                  label: "Total Events",
                  value: stats.totalEvents,
                  icon: (
                    <RiCalendarEventLine
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
                {
                  label: "Completed Events",
                  value: stats.completedEvents,
                  icon: (
                    <RiCheckboxCircleLine
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
                {
                  label: "Upcoming Events",
                  value: stats.upcomingEventsCount,
                  icon: (
                    <RiTimerLine
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
                {
                  label: "Total Inquiries",
                  value: stats.totalInquiries,
                  icon: (
                    <RiQuestionAnswerLine
                      size={30}
                      className="text-primaryBtnHoverColor"
                    />
                  ),
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="bg-white shadow-lg rounded-2xl p-6 flex items-center justify-between border border-primaryTextColor"
                >
                  <div>
                    <p className="text-sm text-secondaryTextColor">
                      {card.label}
                    </p>
                    <h2 className="text-xl font-bold text-primaryTextColor">
                      {card.value}
                    </h2>
                  </div>
                  {card.icon}
                </div>
              ))}
        </div>

        {/* Upcoming Events */}
        <div className="mb-10">
          <h2 className="text-2xl font-heading font-bold text-primaryTextColor mb-6">
            Upcoming Events
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              Array(3)
                .fill(0)
                .map((_, i) => <SkeletonEventCard key={i} />)
            ) : stats?.upcomingEvents?.length === 0 ? (
              <EmptyUpcomingEvents />
            ) : (
              stats?.upcomingEvents?.map((event) => (
                <div
                  key={event._id}
                  className="relative rounded-2xl overflow-hidden shadow-lg hover:scale-[1.02] transition-transform cursor-pointer"
                >
                  {/* Background image */}
                  <div
                    className="w-full h-48 bg-center bg-cover"
                    style={{
                      backgroundImage: `url(${event.field.imageUrl})`,
                    }}
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-40 p-4 flex flex-col justify-end">
                    <h3 className="text-lg font-bold text-white mb-1">
                      {event.eventType === "single"
                        ? `${
                            event.teamOne.charAt(0).toUpperCase() +
                            event.teamOne.slice(1)
                          } vs ${
                            event.teamTwo.charAt(0).toUpperCase() +
                            event.teamOne.slice(1)
                          }`
                        : event.eventType.charAt(0).toUpperCase() +
                          event.eventType.slice(1)}
                    </h3>
                    <p className="text-white text-sm mb-1">
                      Field: {event.field.fieldName}
                    </p>
                    <p className="text-white text-sm mb-1">
                      Date:{" "}
                      {new Date(event?.eventDate).toLocaleDateString(
                        undefined,
                        {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        }
                      )}
                    </p>

                    <p className="text-white text-sm">
                      Status:{" "}
                      <span className="font-semibold capitalize">
                        {event.status}
                      </span>
                    </p>
                  </div>{" "}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-2xl font-heading font-bold text-primaryTextColor mb-6">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {loading
              ? Array(4)
                  .fill(0)
                  .map((_, i) => <SkeletonQuickActionButton key={i} />)
              : quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(action.path)}
                    className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow font-semibold"
                  >
                    {action.label}
                  </button>
                ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
